/**
 * Redux slice for managing organizations and groups.
 *
 * This slice handles:
 *  - CRUD operations for organizations and groups using asynchronous thunks.
 *  - Local storage persistence for organization selection state.
 *  - Merging local state with API responses for group selection states.
 *  - Recursively updating nested group trees (for access roles/permissions).
 *
 * Note: This file uses Redux Toolkit's `createSlice` to simplify reducer logic.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OrganizationSelectionDto,
  organizationState,
} from "./organization.types";
import {
  checkInvitation,
  createOrganization,
  createOrganizationMember,
  deleteOrganization,
  getOrganization,
  getOrganizations,
  leaveOrganization,
  removeOrganizationMember,
  respondToInvitation,
  updateOrganization,
  updateOrganizationMember,
} from "./organization.thunk";
import {
  createGroup,
  createGroupMember,
  deleteGroup,
  getGroup,
  leaveGroup,
  removeGroupMember,
  updateGroup,
  updateGroupMember,
} from "./group.thunk";
import { GroupBranchDto } from "./group.types";
import {
  addElementToTree,
  findElementByGroupId,
  removeElementFromTree,
  replaceElementInTree,
} from "../../helpers/treeHelpers";
import { restoreArchivedTask } from "../tasks/taskThunk";
import { MemberStatus } from "../users/users.types";

/**
 * Save the current organizations state to localStorage.
 *
 * @param state - An array of OrganizationSelectionDto representing the current organizations state.
 */
function saveStateToLocalStorage(state: OrganizationSelectionDto[]) {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("organizationsState", serializedState);
  } catch (error) {
    console.error("Could not save state to localStorage", error);
  }
}

/**
 * A default organization used when no organization is selected.
 */
const noOrganization: OrganizationSelectionDto = {
  id: "0",
  name: "No organization",
  groupBranch: {
    selectionState: 2,
    name: "No organization",
    id: "0",
    organizationId: "0",
    groupMembers: [],
  },
};

/**
 * Load the organizations state from localStorage.
 *
 * @returns An array of OrganizationSelectionDto. If no state is found, returns an empty array.
 */
export function loadOrganizationsStateFromLocalStorage(): OrganizationSelectionDto[] {
  try {
    const serializedState = localStorage.getItem("organizationsState");
    if (serializedState === null) {
      return []; // Return default state if not found
    }
    const organizations = JSON.parse(
      serializedState,
    ) as OrganizationSelectionDto[];
    return organizations;
  } catch (error) {
    console.error("Could not load state from localStorage", error);
    return [];
  }
}

/**
 * Merge the selection state from a locally stored group with the API group data.
 *
 * This function recursively updates the `selectionState` for a group and its children.
 *
 * @param apiGroup - GroupBranchDto object from the API.
 * @param localGroup - Optional GroupBranchDto object from local storage.
 * @returns A new GroupBranchDto with the merged selection state.
 */
function mergeSelectionState(
  apiGroup: GroupBranchDto,
  localGroup?: GroupBranchDto,
): GroupBranchDto {
  const mergedGroup = { ...apiGroup };

  // Use the selectionState from localGroup if it exists
  if (localGroup && localGroup.selectionState !== undefined) {
    mergedGroup.selectionState = localGroup.selectionState;
  }

  // Recursively merge child groups if they exist
  if (apiGroup.groups && apiGroup.groups.length > 0) {
    mergedGroup.groups = apiGroup.groups.map((apiSubGroup) => {
      const localSubGroup = localGroup?.groups?.find(
        (lg) => lg.id === apiSubGroup.id,
      );
      // Pass undefined if localSubGroup is not found
      return mergeSelectionState(apiSubGroup, localSubGroup);
    });
  }

  return mergedGroup;
}

/**
 * Initial state for the organization slice.
 *
 * It includes:
 *  - organizations loaded from localStorage.
 *  - activeAccessRoles: a list of group IDs that are currently active.
 *  - Flags for error, success, and loading states.
 */
const initialState: organizationState = {
  organizations: loadOrganizationsStateFromLocalStorage(),
  pendingOrganizations: [],
  activeAccessRoles: [],
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

/**
 * Recursively sets active access roles based on the selection state of groups.
 *
 * If a group (or any of its child groups) is selected (i.e. selectionState > 0),
 * its id is added to the activeAccessRoles list in the state.
 *
 * @param state - The current organizationState.
 * @param group - The GroupBranchDto to check.
 */
function setAccessRole(state: organizationState, group: GroupBranchDto) {
  // Recursively process child groups
  if (group.groups && group.groups.length > 0) {
    group.groups.forEach((subGroup) => {
      setAccessRole(state, subGroup);
    });
  }

  // If the group is selected (non-zero), add it to activeAccessRoles if not already present.
  if (group.selectionState && group.selectionState > 0) {
    if (state.activeAccessRoles.indexOf(group.id) === -1) {
      state.activeAccessRoles.push(group.id);
    }
  }
}

/**
 * The main organization slice definition using Redux Toolkit's createSlice.
 *
 * It contains:
 *  - Reducers for synchronous actions (e.g. updating role selection state).
 *  - Extra reducers for handling async thunk lifecycle events (pending, fulfilled, rejected).
 */
// Adds a role once: a group can be selected again through its parent
function addAccessRole(state: organizationState, id: string) {
  if (!state.activeAccessRoles.includes(id)) state.activeAccessRoles.push(id);
}

function setGroupSelectionState(
  state: organizationState,
  group: GroupBranchDto,
  selectedState?: number,
): GroupBranchDto {
  const newGroup = { ...group }; // Clone the group to avoid direct state mutation

  if (selectedState !== undefined) {
    newGroup.selectionState = selectedState;
    if (selectedState === 2) {
      addAccessRole(state, newGroup.id);
    } else if (selectedState === 0) {
      state.activeAccessRoles = state.activeAccessRoles.filter(
        (role) => role !== newGroup.id,
      );
    }
  } else {
    // Toggle the selection state based on current value
    switch (newGroup.selectionState) {
      case 2:
        newGroup.selectionState = 0;
        state.activeAccessRoles = state.activeAccessRoles.filter(
          (role) => role !== newGroup.id,
        );
        break;
      default:
        // 1 (partly selected), 0 or unset: select fully
        newGroup.selectionState = 2;
        addAccessRole(state, newGroup.id);
        break;
    }
  }

  // Recursively update child groups if they exist,
  // applying the parent's selection state to all children.
  if (newGroup.groups && newGroup.groups.length > 0) {
    newGroup.groups = newGroup.groups.map((childGroup) =>
      setGroupSelectionState(
        state,
        childGroup,
        newGroup.selectionState === 2 ? 2 : 0,
      ),
    );
  }
  return newGroup;
}

function updateRoleSelectionStateHelper(
  state: organizationState,
  payload: { groupId: string; organizationId: string },
) {
  const { groupId, organizationId } = payload;
  /**
   * Toggle the selection state of a group.
   *
   * If a specific selectedState is provided, use that value.
   * Otherwise, toggle based on the current state:
   *  - If 2 (fully selected), set to 0 (not selected).
   *  - If 1 (partially selected) or 0 (not selected), set to 2 (fully selected).
   *
   * Also update the activeAccessRoles list accordingly.
   *
   * @param group - The current GroupBranchDto.
   * @param selectedState - Optional new selection state.
   * @returns The updated GroupBranchDto.
   */

  /**
   * Recursively search for the group to update and update its selection state.
   *
   * Also, propagate any change upwards to adjust parent group's selection state.
   *
   * @param group - The current GroupBranchDto.
   * @param groupId - The id of the group to update.
   * @returns An object containing the updated group and a flag indicating if an update occurred.
   */
  function updateSelectionState(
    group: GroupBranchDto,
    groupId: string,
  ): { updatedGroup: GroupBranchDto; isUpdated: boolean } {
    let isUpdated = false;
    const newGroup = { ...group }; // Clone to avoid state mutation

    // Base case: If this is the group we want to update, toggle its state.
    if (newGroup.id === groupId) {
      const toggledGroup = setGroupSelectionState(state, newGroup);
      return { updatedGroup: toggledGroup, isUpdated: true };
    }

    // If the group has children, recursively search and update.
    if (newGroup.groups && newGroup.groups.length > 0) {
      let anyChildUpdated = false;

      newGroup.groups = newGroup.groups.map((childGroup) => {
        const { updatedGroup: updatedChildGroup, isUpdated: childUpdated } =
          updateSelectionState(childGroup, groupId);

        if (childUpdated) {
          anyChildUpdated = true;
        }

        return updatedChildGroup;
      });

      if (anyChildUpdated) {
        isUpdated = true;
        // Propagate the change upward by adjusting parent's selection state:
        // Fully selected if all children are selected, otherwise partially selected.
        newGroup.selectionState = newGroup.groups.every(
          (child) => child.selectionState === 2,
        )
          ? 2 // All children are fully selected
          : 1; // Mixed selection states
      }
    }

    return { updatedGroup: newGroup, isUpdated };
  }

  // Update the group branch selection state within the specified organization.
  state.organizations = state.organizations.map((org) => {
    if (org.id === organizationId) {
      const { updatedGroup: updatedGroupBranch } = updateSelectionState(
        org.groupBranch,
        groupId,
      );

      return {
        ...org,
        groupBranch: updatedGroupBranch, // Use the updated group branch
      };
    }
    return org;
  });

  // Persist the updated organizations state to localStorage.
  saveStateToLocalStorage(state.organizations);
}

/**
 * Sets the selection of an organization or group (by id) to `value` - only
 * toggles it when the group's own state differs
 */
function selectRole(state: organizationState, groupId: string, value: number) {
  for (const org of state.organizations) {
    const group =
      org.id === groupId
        ? org.groupBranch
        : findElementByGroupId(org.groupBranch, groupId)?.groupBranch;
    if (group) {
      if (group.selectionState !== value) {
        updateRoleSelectionStateHelper(state, {
          groupId,
          organizationId: org.id,
        });
      }
      return;
    }
  }
}

export const organizationSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    /**
     * Update the selection state for a specific group in an organization.
     *
     * This reducer toggles the selection state of a group and updates the activeAccessRoles.
     * It works recursively, so changes propagate to child groups as needed.
     *
     * @param state - The current organizationState.
     * @param action - Payload containing the groupId to update and the organizationId.
     */

    setNoRoleSelectionState: (
      state,
      action: PayloadAction<{ value: number }>,
    ) => {
      if (
        state.organizations[0].groupBranch.selectionState !==
        action.payload.value
      ) {
        updateRoleSelectionStateHelper(state, {
          groupId: "0",
          organizationId: "0",
        });
      }
    },
    setRoleSelectionState: (
      state,
      action: PayloadAction<{
        groupId: string;
        value: number;
      }>,
    ) => {
      selectRole(state, action.payload.groupId, action.payload.value);
    },
    updateRoleSelectionState: (
      state,
      action: PayloadAction<{ groupId: string; organizationId: string }>,
    ) => {
      updateRoleSelectionStateHelper(state, action.payload);
    },
    resetGroup: (state) => {
      state.group = undefined;
    },
    resetOrganizationStatus: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      state.isError = false;
    },
  },
  extraReducers: (builder) => {
    // ---------------------------
    // Organization CRUD Thunks
    // ---------------------------
    builder

      // Select the role of a restored task, so it shows up in the lists
      .addCase(restoreArchivedTask.fulfilled, (state, action) => {
        const access = action.payload.task.NoteAccess?.[0];
        const roleId = access?.groupId || access?.organizationId;
        if (roleId) selectRole(state, roleId, 2);
      })

      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Create a new organization with a default selection state
        const newOrganization = {
          ...action.payload,
          selectionState: 1,
        };
        // Add the new organization to the beginning of the organizations array
        state.organizations = [...state.organizations, newOrganization];
        state.activeAccessRoles = [
          ...(state.activeAccessRoles || []),
          newOrganization.id,
        ];
        saveStateToLocalStorage(state.organizations);
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(leaveOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Remove the organization that was left.
        state.organizations = state.organizations.filter(
          (orga) => orga.id != action.payload,
        );
      })
      .addCase(leaveOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(leaveOrganization.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(removeGroupMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.group?.groupMembers) {
          state.group.groupMembers = [...state.group.groupMembers].filter(
            (member) => member.id !== action.payload.userId,
          );
        }
      })
      .addCase(removeGroupMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(removeGroupMember.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(updateGroupMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.group?.groupMembers) {
          state.group.groupMembers = [...state.group.groupMembers].map(
            (member) => {
              if (member.id === action.payload.id) {
                return { ...member, ...action.payload };
              }
              return member;
            },
          );
        }
      })
      .addCase(updateGroupMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateGroupMember.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(respondToInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Update the member's status in the organization members list.
        const updatedOrg = state.pendingOrganizations.find(
          (org) => org.id === action.meta.arg.organizationId,
        );

        if (updatedOrg) {
          const newOrganization = {
            ...updatedOrg,
            selectionState: 1,
          };

          if (action.payload === "DECLINED") {
            state.pendingOrganizations = state.pendingOrganizations.filter(
              (org) => org.id !== action.meta.arg.organizationId,
            );
          } else if (action.payload === "ACCEPTED") {
            state.organizations = [...state.organizations, newOrganization];
            state.pendingOrganizations = state.pendingOrganizations.filter(
              (org) => org.id !== action.meta.arg.organizationId,
            );
          }
        }
      })
      .addCase(respondToInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(respondToInvitation.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(updateOrganizationMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.organization?.organizationMembers) {
          state.organization.organizationMembers = [
            ...state.organization.organizationMembers,
          ].map((member) => {
            if (member.id === action.payload.id) {
              return { ...member, ...action.payload };
            }
            return member;
          });
        }
      })
      .addCase(updateOrganizationMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateOrganizationMember.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(removeOrganizationMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.organization?.organizationMembers) {
          state.organization.organizationMembers = [
            ...state.organization.organizationMembers,
          ].filter((member) => member.id !== action.payload.userId);
        }
      })
      .addCase(removeOrganizationMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(removeOrganizationMember.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Group CRUD Thunks - leaveGroup
      // ---------------------------
      .addCase(leaveGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        // Remove the group from the organization's group tree.
        state.organizations = state.organizations.map((org) => {
          if (org.id === action.payload.organizationId) {
            const oldTree = org.groupBranch.groups;
            if (!oldTree) return org;
            return {
              ...org,
              groupBranch: {
                ...org.groupBranch,
                groups: removeElementFromTree(oldTree, action.payload.groupId),
              },
            };
          } else return org;
        });
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(leaveGroup.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Organization Thunks: getOrganization
      // ---------------------------
      .addCase(getOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.organization = action.payload;
      })
      .addCase(getOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getOrganization.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Organization Thunks: getOrganizations
      // ---------------------------
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Load the previously persisted local state.
        const localOrganizations = loadOrganizationsStateFromLocalStorage();

        const memberOrganizations = action.payload.memberOrganizationsMap.sort(
          (a, b) => a.name.localeCompare(b.name),
        );
        // Prepend the default organization and merge API data with local state.
        state.organizations = [noOrganization, ...memberOrganizations].map(
          (organization) => {
            // Find the corresponding organization in the locally stored organizations.
            const localOrg = localOrganizations.find(
              (localOrg) => localOrg.id === organization.id,
            );

            let mergedGroupBranch = organization.groupBranch;
            if (localOrg) {
              // Merge the selection state recursively between API data and local state.
              mergedGroupBranch = mergeSelectionState(
                organization.groupBranch,
                localOrg.groupBranch,
              );
            }
            return {
              ...organization,
              groupBranch: mergedGroupBranch,
            };
          },
        );

        // Set the active access roles based on the updated group tree.
        state.organizations.forEach((orga) => {
          setAccessRole(state, orga.groupBranch);
        });

        state.pendingOrganizations = action.payload.pendingOrgs || [];

        state.organizations = state.organizations.sort((a, b) => {
          if (a.updatedAt && b.updatedAt)
            return (
              new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
            );
          else {
            return 0;
          }
        });
      })
      .addCase(getOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getOrganizations.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Organization Thunks: updateOrganization
      // ---------------------------
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.organization) {
          state.organization = { ...state.organization, ...action.payload };
        }
        // Update the organization in the organizations array.
        state.organizations = state.organizations.map((orga) =>
          orga.id === action.payload.id
            ? {
                ...orga,
                ...action.payload,
                selectionState: orga.selectionState,
              }
            : orga,
        );
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Organization Thunks: deleteOrganization
      // ---------------------------
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.organization = undefined;
        // Remove the deleted organization from the organizations array.
        state.organizations = state.organizations.filter(
          (orga) => orga.id != action.payload,
        );
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteOrganization.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Group Thunks: createGroup
      // ---------------------------
      .addCase(createGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const newGroup: GroupBranchDto = {
          ...action.payload,
          selectionState: 1, // default selection state upon creation
        };
        // If the new group belongs to the currently loaded organization, update the group tree.
        if (state?.organization?.id === newGroup.organizationId) {
          if (state.organization.groupBranch.groups) {
            state.organization.groupBranch.groups = addElementToTree(
              state.organization.groupBranch.groups,
              newGroup,
            );
          }
        }

        state.activeAccessRoles.push(newGroup.id);
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createGroup.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Group Thunks: updateGroup
      // ---------------------------
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const updatedGroup: GroupBranchDto = {
          ...action.payload,
          selectionState: 0, // Reset selection state after update
        };

        // Update the group in the organization's group tree if applicable.
        if (state?.organization?.id === updatedGroup.organizationId) {
          state.organization.groupBranch.groups = replaceElementInTree(
            state.organization.groupBranch.groups as GroupBranchDto[],
            updatedGroup,
          );
        }

        if (state.group && state.group.id === updatedGroup.id) {
          state.group = { selectionState: 0, ...action.payload };
        }
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateGroup.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // ---------------------------
      // Group Thunks: getGroup
      // ---------------------------
      .addCase(getGroup.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Set the fetched group with a default selection state.
        state.group = { ...action.payload, selectionState: 0 };
      })

      // ---------------------------
      // Group Thunks: deleteGroup
      // ---------------------------
      .addCase(deleteGroup.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Clear the currently loaded group.
        state.group = undefined;

        // Remove the deleted group from the organization's group tree.
        if (state?.organization) {
          state.organization.groupBranch.groups = removeElementFromTree(
            state.organization.groupBranch.groups as GroupBranchDto[],
            action.payload, // groupID to be removed
          );
        }
      })
      .addCase(createGroupMember.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createGroupMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createGroupMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.group?.groupMembers) {
          state.group.groupMembers = [
            ...state.group.groupMembers,
            action.payload,
          ];
        }
      })
      .addCase(createOrganizationMember.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createOrganizationMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createOrganizationMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Add the new organization member to the current organization.
        if (state.organization) {
          state.organization.organizationMembers = [
            ...state.organization.organizationMembers,
            {
              email: action.payload,
              role: "GUEST",
              firstName: action.payload,
              lastName: "",
              id: "",
              userName: "",
              status: MemberStatus.PENDING,
            },
          ];
        }
      })
      .addCase(checkInvitation.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(checkInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(checkInvitation.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      });
  },
});

// Export the synchronous action for updating role selection state.
export const {
  updateRoleSelectionState,
  resetOrganizationStatus,
  setNoRoleSelectionState,
  resetGroup,
  setRoleSelectionState,
} = organizationSlice.actions;

// Export the reducer to be used in the store configuration.
export default organizationSlice.reducer;
